import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { Calendar, CalendarPlus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Skeleton } from '../../components/ui/skeleton';
import { getEvents } from './eventsAPI';
import { RootState } from '../../reducers';
import MeetingRecorder from './MeetingRecorder';
import MacAppPromotionBanner from '../../components/common/MacAppPromotionBanner';

interface EventsDialogProps {
  open: boolean;
  onClose: () => void;
}

const EventsDialog: React.FC<EventsDialogProps> = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { results } = useSelector((state: RootState) => state.events);

  useEffect(() => {
    if (open && !results) {
      dispatch(getEvents());
    }
  }, [open, dispatch, results]);

  const provideName = (provider: 'google' | 'microsoft' | 'zoom') => {
    switch (provider) {
      case 'google':
        return t('events.list.providers.google');
      case 'microsoft':
        return t('events.list.providers.microsoft');
      case 'zoom':
        return t('events.list.providers.zoom');
      default:
        return null;
    }
  };

  const eventsList =
    results?.events.filter((ev) => {
      switch (ev.provider) {
        case 'google':
          return results.isGoogleCalendarConnected;
        case 'microsoft':
          return results.isMicrosoftCalendarConnected;
        case 'zoom':
          return results.isZoomMeetingsConnected;
        default:
          return false;
      }
    }) || [];

  const renderContent = () => {
    if (!results) {
      return (
        <div className="space-y-6">
          <div>
            <Skeleton className="h-5 w-32 mb-3" />
            <Skeleton className="h-10 w-full" />
          </div>
          <div className="rounded-lg border bg-card overflow-hidden">
            <div className="px-4 py-3 border-b bg-muted/40">
              <Skeleton className="h-4 w-24" />
            </div>
            <div className="divide-y">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="px-4 py-3 flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-5 w-5 rounded" />
                    <div>
                      <Skeleton className="h-4 w-48 mb-1" />
                      <Skeleton className="h-3 w-32" />
                    </div>
                  </div>
                  <Skeleton className="h-8 w-20" />
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }

    return (
      <>
        {/* Meeting Recorder Section - Primary CTA */}
        <div>
          <div className="mb-3">
            <h3 className="text-base font-semibold mb-1">
              {t('events.recorder.title')}
            </h3>
            <p className="text-sm text-muted-foreground">
              {t('events.recorder.description')}
            </p>
          </div>
          <MeetingRecorder variant="inline" />
        </div>

        {/* Events List Section */}
        {results.askToConnectCalendar ||
        (!results.isGoogleCalendarConnected &&
          !results.isMicrosoftCalendarConnected &&
          !results.isZoomMeetingsConnected) ? (
          <div className="rounded-lg border bg-card p-6">
            <div className="flex flex-col items-center gap-4 py-4">
              <Calendar className="w-10 h-10 text-primary" />
              <div className="text-center space-y-2">
                <h3 className="text-base font-semibold">
                  {t('events.calendar.connect.title')}
                </h3>
                <p className="text-sm text-muted-foreground max-w-sm">
                  {t('events.calendar.connect.description')}
                </p>
              </div>
              <Button
                variant="default"
                size="default"
                onClick={() => {
                  onClose();
                  navigate('/settings/meetingbot');
                }}
              >
                <CalendarPlus className="mr-2 h-4 w-4" />
                {t('events.calendar.connect.button')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="rounded-lg border bg-card overflow-hidden">
            {/* Events List Header */}
            <div className="grid grid-cols-[1fr_auto] items-center gap-4 px-4 py-3 border-b bg-muted/40">
              <p className="font-medium text-sm">{t('events.list.title')}</p>
              <p className="font-medium text-sm">{t('events.list.join')}</p>
            </div>

            {/* Events List */}
            <div className="divide-y max-h-[300px] overflow-y-auto">
              {eventsList.length === 0 ? (
                <div className="px-4 py-8 text-center">
                  <p className="text-sm text-muted-foreground">
                    {t('events.list.empty')}
                  </p>
                </div>
              ) : (
                eventsList.map((event) => (
                  <div
                    key={`${event.provider}${event.eventId}`}
                    className="grid grid-cols-[1fr_auto] items-center gap-4 px-4 py-3 hover:bg-muted/20 transition-colors"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <Calendar className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">
                          {event.name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {moment(event.startDateTime).format('MMM D h:mm A')}
                        </p>
                      </div>
                    </div>
                    <Button asChild variant="outline" size="sm">
                      <a
                        href={event.webLink}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Calendar className="mr-2 h-3.5 w-3.5" />
                        {provideName(event.provider)}
                      </a>
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-3xl max-h-[70vh] p-0 overflow-hidden flex flex-col">
          <DialogHeader className="px-6 pt-4 pb-2 flex-shrink-0">
            <DialogTitle>{t('drawer.events.title')}</DialogTitle>
          </DialogHeader>

          <div className="px-6">
            <MacAppPromotionBanner variant="meetings" />
          </div>

          <ScrollArea className="flex-1">
            <div className="px-6 pt-0 pb-6 space-y-4">{renderContent()}</div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EventsDialog;
